import { sharedFeature } from 'src/app/shared/ngrx/shared.state';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  input,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import * as printJSModule from 'print-js';
import { Subject } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';

import {
  PDFDocumentProxy,
  PdfViewerComponent,
  PdfViewerModule,
} from 'ng2-pdf-viewer';
import { LOCAL_STORAGE_KEY } from 'src/app/app.config';
import { State } from 'src/app/reducers';
import { LangButton } from 'src/app/shared/Models/langButton';
import {
  clearPreviewHtml,
  fetchPreviewHtml,
  clearPreviewPdfHtml,
  fetchPreviewPdfHtml,
  handleHttpError,
  setPreviewFailed,
} from 'src/app/shared/ngrx/shared.actions';
import { FileService } from 'src/app/shared/Services/files/files.service';
import { UtilsService } from 'src/app/shared/Services/utils/utils.service';
import { getFullURL } from 'src/app/shared/utils.functions';
import { AsyncPipe } from '@angular/common';
import { SpinnerComponent } from '../spinner/spinner.component';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DIALOG_DATA, MatDialogActions } from '@angular/material/dialog';
import { HtmlPreviewComponent } from './html-preview/html-preview.component';
import { PageSelectorDirective } from './page-selector.directive';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { LanguageButtonsComponent } from '../language-buttons/language-buttons.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PdfHtmlPreviewComponent } from './pdf-html-preview/pdf-html-preview.component';
import { ContentLanguage } from '../../constants/languages';

// Unwrap nested default exports that can occur with CommonJS/ESM interop
function unwrapDefault(module: any): any {
  let result = module;
  while (result && typeof result !== 'function' && result.default) {
    result = result.default;
  }
  return result;
}
const printJS: (config: any) => void = unwrapDefault(printJSModule);

interface PreviewDialogData {
  url: string;
  params: any;
  baseLanguage: string;
  numberLanguages: number;
  langs: LangButton[];
  multiRequired: boolean;
  hideLangs: boolean;
  htmlPreview: boolean;
  action: string;
  blob: Blob;
  blobUrl: string;
}

@Component({
  selector: 'app-preview',
  templateUrl: './pdf-preview.component.html',
  styleUrls: ['./pdf-preview.component.scss'],
  imports: [
    MatProgressSpinnerModule,
    LanguageButtonsComponent,
    MatButtonToggleModule,
    ReactiveFormsModule,
    MatButtonModule,
    PageSelectorDirective,
    PdfViewerModule,
    HtmlPreviewComponent,
    MatDialogActions,
    MatIconModule,
    SpinnerComponent,
    AsyncPipe,
    TranslocoPipe,
    PdfHtmlPreviewComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewComponent implements OnInit, OnDestroy, OnChanges {
  private cdr = inject(ChangeDetectorRef);
  private fileService = inject(FileService);
  private ngrxStore = inject<Store<State>>(Store);
  private translate = inject(TranslocoService);
  private utils = inject(UtilsService);
  private destroyRef = inject(DestroyRef);
  protected data = inject<PreviewDialogData>(MAT_DIALOG_DATA, {
    optional: true,
  });
  private domainName = window.origin;

  readonly action = input('');
  readonly blob = input<Blob>(undefined);
  readonly blobUrl = input<string>(undefined);
  readonly hideButtons = input<boolean>(undefined);
  readonly langs = input<LangButton[]>(undefined);
  readonly multiRequired = input<boolean>(undefined);
  readonly nonModal = input<boolean>(undefined);
  readonly numberLanguages = input(1);
  readonly params = input<any>({});
  readonly url = input<string>(undefined);
  readonly baseLanguage = input<string>(undefined);
  readonly showRefresh = input<boolean>(undefined);
  readonly showFormat = input<boolean>(undefined);
  readonly hideLangs = input(false);
  readonly htmlPreview = input<boolean>(true);

  public page = 1;
  readonly src = signal<
    | string
    | {
        url: string;
        withCredentials: boolean;
        httpHeaders: object;
      }
  >('');

  cachedBlobUrl: string;
  debouncer$ = new Subject<void>();
  formatControl = new FormControl<'html' | 'pdf'>('pdf');
  holdPage = false;
  readonly isDownloading = signal(false);
  langParams: { base_lang?: ContentLanguage; lang?: ContentLanguage[] } = {};
  readonly show = signal(false);

  previewHtml$ = this.ngrxStore.select(sharedFeature.selectPreviewHtml);
  previewPdfHtml$ = this.ngrxStore.select(sharedFeature.selectPreviewPdfHtml);
  readonly previewFailed = toSignal(
    this.ngrxStore.select(sharedFeature.selectPreviewFailed),
    { initialValue: false },
  );

  readonly pdfComponent = viewChild(PdfViewerComponent);
  readonly print = viewChild<ElementRef>('print');
  private messageHandler: (event: MessageEvent) => void;

  constructor() {
    const hasBlob = this.data?.blob ?? this.blob();
    this.show.set(hasBlob ? true : false);
  }

  ngOnInit(): void {
    // Language changes: debounce rapid changes, then refetch
    this.debouncer$
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refetchCurrentFormat());

    // Format switch: load from cache or fetch if needed
    this.formatControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadFormatIfNeeded());

    const url = this.data?.url ?? this.url();
    const langs = this.data?.langs ?? this.langs();

    // Set up initial lang params
    if (langs) {
      this.langParams = this.utils.getParams(
        langs,
        this.data?.baseLanguage ?? this.baseLanguage(),
        null,
        null,
      ) as { base_lang?: ContentLanguage; lang?: ContentLanguage[] };
    }

    // Initial load
    if (url) {
      this.fetchCurrentFormat();
    } else if (this.data?.blob ?? this.blob()) {
      this.renderBlob();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('langs' in changes && this.langs()) {
      this.clearHtml();
    }
  }

  private clearHtml(): void {
    this.ngrxStore.dispatch(clearPreviewHtml());
  }

  private clearPdfHtml(): void {
    this.ngrxStore.dispatch(clearPreviewPdfHtml());
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
    }
  }

  constructUrl = (): string => {
    const blobUrl = this.data?.blobUrl ?? this.blobUrl();
    if (blobUrl) return blobUrl;
    if (this.data?.action ?? this.action())
      return `${this.data?.url ?? this.url()}${this.data?.action ?? this.action()}/`;
    return this.data?.url ?? this.url();
  };

  fetchHtml(): void {
    this.ngrxStore.dispatch(
      fetchPreviewHtml({ url: this.data?.url ?? this.url() }),
    );
  }

  fetchPdfHtml() {
    this.messageHandler = (event: MessageEvent) => {
      if (
        event.origin === this.domainName &&
        event.data.name === 'pagedJsInit'
      ) {
        this.show.set(true);
        this.cdr.markForCheck();
      }
    };
    window.addEventListener('message', this.messageHandler);

    this.ngrxStore.dispatch(
      fetchPreviewPdfHtml({
        url: this.constructUrl(),
        params: {
          ...(this.data?.params ?? this.params()),
          ...this.langParams,
          response: 'html',
          pdf_mode: true,
        },
      }),
    );
  }

  fetchPdf(holdPage: boolean): void {
    this.holdPage = holdPage;
    this.src.set({
      url: getFullURL(this.constructUrl(), {
        ...(this.data?.params ?? this.params()),
        ...this.langParams,
      }),
      withCredentials: true,
      httpHeaders: {
        Authorization: 'Token ' + localStorage.getItem(LOCAL_STORAGE_KEY),
      },
    });
  }

  private fetchPdfAndPrint(): void {
    this.fileService
      .fetchRenderedTemplate(this.constructUrl(), {
        ...(this.data?.params ?? this.params()),
        ...this.langParams,
        print: true,
      })
      .subscribe({
        next: (result) => {
          this.printPdf(result);
        },
        error: (error) => {
          this.ngrxStore.dispatch(handleHttpError({ error }));
        },
      });
  }

  downloadPdf = (): void => {
    this.isDownloading.set(true);
    this.fileService
      .downloadFile(this.constructUrl(), {
        ...(this.data?.params ?? this.params()),
        ...this.langParams,
        download: true,
      })
      .add(() => {
        this.isDownloading.set(false);
      });
  };

  langsChanged(langs: LangButton[]): void {
    this.langParams = this.utils.getParams(
      langs,
      this.data?.baseLanguage ?? this.baseLanguage(),
      null,
      null,
    ) as { base_lang?: ContentLanguage; lang?: ContentLanguage[] };
    // Clear cache since params changed, then debounce refetch
    this.clearCurrentFormatCache();
    this.debouncer$.next();
  }

  private clearCurrentFormatCache(): void {
    if (this.formatControl.value === 'pdf') {
      this.ngrxStore.dispatch(clearPreviewPdfHtml());
    } else {
      this.ngrxStore.dispatch(clearPreviewHtml());
    }
  }

  private loadFormatIfNeeded(): void {
    const isPdf = this.formatControl.value === 'pdf';
    const useHtmlPreview = this.data?.htmlPreview ?? this.htmlPreview();

    if (isPdf && useHtmlPreview) {
      // Check if PDF HTML is already cached
      this.previewPdfHtml$.pipe(take(1)).subscribe((html) => {
        if (html === null) {
          this.show.set(false);
          this.fetchPdfHtml();
        }
      });
    } else if (isPdf) {
      this.fetchPdf(false);
    } else {
      // Check if HTML is already cached
      this.previewHtml$.pipe(take(1)).subscribe((html) => {
        if (html === null) {
          this.fetchHtml();
        }
      });
    }
  }

  private fetchCurrentFormat(): void {
    this.show.set(false);
    const isPdf = this.formatControl.value === 'pdf';
    const useHtmlPreview = this.data?.htmlPreview ?? this.htmlPreview();

    if (isPdf && useHtmlPreview) {
      this.fetchPdfHtml();
    } else if (isPdf) {
      this.fetchPdf(false);
    } else {
      this.fetchHtml();
    }
  }

  private refetchCurrentFormat(): void {
    this.show.set(false);
    this.fetchCurrentFormat();
  }

  pageRendered(): void {
    this.pdfComponent().pdfViewer.scroll.down = false;
  }

  /**
   * ng2-pdf-viewer fetches the document itself, so a failed render never passes
   * through the ngrx effects. Swallowing the event left the spinner running with
   * nothing behind it.
   */
  onPdfViewerError(): void {
    this.ngrxStore.dispatch(setPreviewFailed());
  }

  printDocument(): Promise<void> | void {
    const blob = this.data?.blob ?? this.blob();
    if (blob) {
      return this.printBlob(blob);
    }
    if (this.cachedBlobUrl) {
      return this.printPdf(this.cachedBlobUrl);
    }
    this.fetchPdfAndPrint();
  }

  private async printBlob(blob: Blob): Promise<void> {
    const base64 = await this.utils.blobToBase64(blob);
    printJS({
      printable: base64,
      type: 'image',
      base64: true,
      onError: (error) => {
        Sentry.captureException(error);
      },
    });
  }

  private printPdf(url: string): void {
    const modalMessage = this.translate.translate('app.pdf-loading-overlay');
    printJS({
      printable: url,
      type: 'pdf',
      showModal: true,
      modalMessage,
      onError: (error) => {
        Sentry.captureException(error);
      },
    });
  }

  refresh(holdPage = false): void {
    this.show.set(false);
    const isPdf = this.formatControl.value === 'pdf';
    const useHtmlPreview = this.data?.htmlPreview ?? this.htmlPreview();

    if (isPdf && useHtmlPreview) {
      this.ngrxStore.dispatch(clearPreviewPdfHtml());
      this.fetchPdfHtml();
    } else if (isPdf) {
      this.fetchPdf(holdPage);
    } else {
      this.ngrxStore.dispatch(clearPreviewHtml());
      this.fetchHtml();
    }
  }

  private renderBlob(): void {
    const reader = new FileReader();
    reader.readAsDataURL(this.data?.blob ?? this.blob());
    reader.onloadend = () => {
      this.src.set(reader.result as string);
      // The dialog host is OnPush, so the async FileReader result needs an
      // explicit markForCheck to render without a later user interaction.
      this.cdr.markForCheck();
    };
  }

  renderPdf = async (event: PDFDocumentProxy): Promise<void> => {
    const data = await event.getData();
    this.cachedBlobUrl = URL.createObjectURL(
      new Blob([data as any], { type: 'octet/stream' }),
    );
    this.show.set(true);
    this.pageRendered();
  };

  ngOnDestroy(): void {
    this.clearHtml();
    this.clearPdfHtml();
  }
}
